/**
 * DevDoc — public Integration Guide page
 * ─────────────────────────────────────────
 * URL:  /dev/:pageId?token=<postbackSecret>
 *
 * Audience: the destination site's dev / integration team. They are
 * NOT users of our platform, so this page is intentionally:
 *   - public (no login wrapper),
 *   - self-contained (everything they need on one URL),
 *   - copy-friendly (every code block has a copy button + the whole
 *     doc can be exported as Markdown for pasting into Slack/email).
 *
 * Access control:
 *   The token in `?token=` must equal the page's `postbackSecret`
 *   stored under `landing_pages.settings.affiliate.postbackSecret`.
 *   Why this is acceptable risk: the doc literally CONTAINS that
 *   secret in the postback URLs, so any leak of the doc URL == leak
 *   of the secret. The operator can revoke all stale doc links at
 *   once via the existing "Rotate secret" button (PUT
 *   /api/capi/affiliate-config/:pageId with `{ rotate: true }`).
 *
 * Layout:
 *   - Header with page title + status pills (Pixel ID / GA4 ID
 *     configured)
 *   - 5 sections, anchored:
 *       1. Overview — what this integration achieves
 *       2. Postback URLs (with "Try it" buttons for live sanity check)
 *       3. Capturing the click_id
 *       4. Site-side browser tracking (HEAD + signup + deposit)
 *       5. Attribution params we forward
 *   - Footer with "Copy doc as Markdown" + "Download .md" buttons
 *
 * What this page does NOT include:
 *   - The "Test events" tab to fire dummy CAPI events to Meta. That
 *     requires a real click row in our DB and Meta token credentials,
 *     and exposing it on a public page would be a spam vector.
 *     Operators send test events from the authenticated Builder UI.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Copy,
  CheckCircle2,
  AlertCircle,
  Download,
  ExternalLink,
  PlayCircle,
  Loader2,
  ShieldCheck,
  Code2,
  Webhook,
  KeyRound,
  Network,
  FileJson,
  type LucideIcon,
} from 'lucide-react';
import {
  buildGtmHeadSnippet,
  buildGtmRegistrationSnippet,
  buildGtmDepositSnippet,
} from '../lib/destinationSnippets';
import { downloadGtmContainerTemplate } from '../lib/gtmContainerTemplate';

// IMPORTANT: VITE_API_URL is configured WITH the `/api` suffix in the
// rest of the codebase (e.g. `https://api.hitzlink.com/api`). Other
// callers concatenate as `${API_URL}/pages`, `${API_URL}/capi/...`.
// Concatenating `${API_URL}/api/capi/...` here produced double `/api`
// in production → 404. Default fallback also includes `/api` so local
// dev "just works" without VITE_API_URL override.
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface DevDocConfig {
  page: { id: string; title: string; slug: string | null };
  affiliate: {
    clickIdPlaceholder: string;
    defaultCurrency: string;
    defaultRegistrationValue: number;
    defaultPurchaseValue: number;
    eventMap: Record<string, string> | null;
  };
  postbackUrls: {
    registration: string;
    ftd: string;
    purchase: string;
    custom: string;
  };
  tracking: {
    primaryPixelId: string;
    configuredPixelIds: string[];
    googleAnalyticsId: string;
    /** GTM container ID configured for this page. Empty string when
     *  the operator hasn't enabled GTM in Builder — the GTM-mode
     *  section then shows a "set up GTM first" prompt. */
    googleTagManagerId: string;
    /** GTM auto-sync state (Phase 3). When `linked: true` the operator
     *  is provisioning their GTM workspace via HitzLink's API; the
     *  Dev Doc renders an "Auto-synced" badge so the dev team knows
     *  the tags they see in GTM are managed by HitzLink. */
    gtmAutoSync?: {
      linked: boolean;
      lastSyncAt: string | null;
      summary: {
        created: number;
        updated: number;
        skipped: number;
        deleted: number;
        errorCount: number;
      } | null;
    };
  };
  apiBaseUrl: string;
}

interface TestResult {
  ok: boolean;
  url: string;
  status: number;
  body: string;
  isExpectedClickNotFound: boolean;
}

const SECTION_NAV = [
  { id: 'overview', navKey: 'overview' as const, icon: ShieldCheck },
  { id: 'postback', navKey: 'postback' as const, icon: Webhook },
  { id: 'click-id', navKey: 'clickId' as const, icon: KeyRound },
  { id: 'browser', navKey: 'browser' as const, icon: Code2 },
  { id: 'attribution', navKey: 'attribution' as const, icon: Network },
] as const;

export function DevDoc() {
  const { t } = useTranslation('devDoc');
  const { pageId } = useParams<{ pageId: string }>();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [config, setConfig] = useState<DevDocConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const [activeSection, setActiveSection] = useState<string>('overview');

  useEffect(() => {
    if (!pageId || !token) {
      setError({ status: 400, message: 'Missing pageId or token in URL' });
      setLoading(false);
      return;
    }
    void loadConfig(pageId, token);
  }, [pageId, token]);

  // Highlight the section currently in view in the side nav. We use
  // IntersectionObserver instead of scroll math so it stays accurate
  // when the user uses anchor jumps (which can be smoother than
  // scrolling). Threshold tuned so a section "wins" once its top
  // crosses ~30% of the viewport — feels natural at typical reading
  // speed without flickering between adjacent sections.
  useEffect(() => {
    if (!config) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveSection(visible.target.id);
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_NAV.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [config]);

  async function loadConfig(id: string, tok: string) {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(
        `${API_URL}/capi/dev-doc/${id}?token=${encodeURIComponent(tok)}`
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        setError({ status: res.status, message: body.error || `HTTP ${res.status}` });
        setConfig(null);
        return;
      }
      const data: DevDocConfig = await res.json();
      setConfig(data);
    } catch (e) {
      setError({
        status: 0,
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <DocShell>
        <div className="flex items-center gap-3 text-gray-400">
          <Loader2 className="animate-spin" size={18} />
          <span>{t('loading')}</span>
        </div>
      </DocShell>
    );
  }

  if (error || !config) {
    return (
      <DocShell>
        <ErrorView error={error} />
      </DocShell>
    );
  }

  return (
    <DocShell>
      <DocContent
        config={config}
        pageId={pageId!}
        token={token}
        activeSection={activeSection}
      />
    </DocShell>
  );
}

// ────────────────────────────────────────────────────────────────────
//  Shell
// ────────────────────────────────────────────────────────────────────

function DocShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('devDoc');
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <header className="sticky top-0 z-30 border-b border-gray-800 bg-gray-950/90 backdrop-blur supports-[backdrop-filter]:bg-gray-950/70">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-blue-500 to-purple-500 text-sm font-bold">
              H
            </div>
            <div>
              <div className="text-sm font-semibold leading-tight">HitzLink</div>
              <div className="text-[10px] text-gray-500 leading-tight">{t('header.brandSubtitle')}</div>
            </div>
          </div>
          <div className="text-[11px] text-gray-500">{t('header.audience')}</div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">{children}</main>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
//  Error / loading states
// ────────────────────────────────────────────────────────────────────

function ErrorView({ error }: { error: { status: number; message: string } | null }) {
  const { t } = useTranslation('devDoc');
  const friendly = useMemo(() => {
    if (!error) return t('error.unknown');
    if (error.status === 401) {
      return t('error.friendly401');
    }
    if (error.status === 403) {
      return t('error.friendly403');
    }
    if (error.status === 404) {
      return t('error.friendly404');
    }
    if (error.status === 400) {
      return error.message;
    }
    return t('error.friendlyGeneric', { message: error.message });
  }, [error, t]);

  return (
    <div className="mx-auto max-w-2xl rounded-xl border border-red-500/30 bg-red-500/5 p-6">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 flex-shrink-0 text-red-400" size={20} />
        <div>
          <h2 className="text-base font-semibold text-red-200">{t('error.title')}</h2>
          <p className="mt-1 text-sm text-red-200/80">{friendly}</p>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
//  Doc content
// ────────────────────────────────────────────────────────────────────

function DocContent({
  config,
  pageId,
  token,
  activeSection,
}: {
  config: DevDocConfig;
  pageId: string;
  token: string;
  activeSection: string;
}) {
  const { t } = useTranslation('devDoc');

  // The cookie bridge needs the *bare* URL param name (no braces) — the
  // affiliate config stores it in placeholder form like "{click_id}".
  const clickIdParam = useMemo(
    () => config.affiliate.clickIdPlaceholder.replace(/[{}]/g, ''),
    [config.affiliate.clickIdPlaceholder]
  );

  // Browser-side tracking is GTM-only — the previous "Direct Pixel + GA4"
  // mode was removed (2026-04). Operators wire Pixel + GA4 inside their
  // GTM container (auto-synced by HitzLink, see GtmAutoSyncPanel) so
  // there's no inline-vendor-SDK fallback path here.
  const activeSnippets = useMemo(
    () => ({
      head: buildGtmHeadSnippet(config.tracking.googleTagManagerId, clickIdParam),
      registration: buildGtmRegistrationSnippet(),
      deposit: buildGtmDepositSnippet(),
    }),
    [config.tracking.googleTagManagerId, clickIdParam]
  );

  const docMarkdown = useMemo(
    () => buildFullMarkdownDoc(config, { snippets: activeSnippets }, t),
    [config, activeSnippets, t]
  );

  return (
    <div className="grid gap-8 lg:grid-cols-[14rem_1fr]">
      {/* ── Side nav ── */}
      <aside className="hidden lg:block">
        <nav className="sticky top-20 space-y-1">
          {SECTION_NAV.map(({ id, navKey, icon: Icon }) => (
            <a
              key={id}
              href={`#${id}`}
              className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition ${
                activeSection === id
                  ? 'bg-blue-500/10 text-blue-300'
                  : 'text-gray-400 hover:bg-gray-800/50 hover:text-gray-200'
              }`}
            >
              <Icon size={14} className="flex-shrink-0" />
              {t(`nav.${navKey}`)}
            </a>
          ))}
          <div className="pt-3" />
          <ExportButtons docMarkdown={docMarkdown} pageTitle={config.page.title} />
        </nav>
      </aside>

      {/* ── Doc body ── */}
      <article className="max-w-3xl space-y-12">
        {/* Title block */}
        <header>
          <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
            <span>{t('breadcrumb')}</span>
            <span>·</span>
            <code className="text-gray-400">{config.page.slug || config.page.id.slice(0, 8)}</code>
          </div>
          <h1 className="text-3xl font-bold text-white sm:text-4xl">
            {config.page.title}
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-gray-400 leading-relaxed">{t('intro')}</p>
          <StatusPills config={config} />
          <div className="mt-4 lg:hidden">
            <ExportButtons docMarkdown={docMarkdown} pageTitle={config.page.title} />
          </div>
        </header>

        <Section id="overview" title={t('sections.overview.title')} icon={ShieldCheck}>
          <p>{t('sections.overview.p1')}</p>
          <p>{t('sections.overview.p2')}</p>
        </Section>

        <Section id="postback" title={t('sections.postback.title')} icon={Webhook}>
          <p>{t('sections.postback.intro')}</p>
          <PostbackRow
            label={t('sections.postback.registrationLabel')}
            description={t('sections.postback.registrationDesc')}
            url={config.postbackUrls.registration}
            event="registration"
            pageId={pageId}
            token={token}
            accent="green"
          />
          <PostbackRow
            label={t('sections.postback.ftdLabel')}
            description={t('sections.postback.ftdDesc')}
            url={config.postbackUrls.ftd}
            event="ftd"
            pageId={pageId}
            token={token}
            accent="purple"
          />
          <details className="rounded-lg border border-gray-700/50 bg-gray-800/30 p-3">
            <summary className="cursor-pointer text-sm text-gray-300 hover:text-white">
              {t('sections.postback.advancedSummary')}
            </summary>
            <div className="mt-3 space-y-3">
              <PostbackRow
                label={t('sections.postback.purchaseLabel')}
                description={t('sections.postback.purchaseDesc')}
                url={config.postbackUrls.purchase}
                event="purchase"
                pageId={pageId}
                token={token}
                accent="gray"
              />
              <CodeBlock
                label={t('sections.postback.customTemplateLabel')}
                code={config.postbackUrls.custom}
                hint={t('sections.postback.customTemplateHint')}
              />
            </div>
          </details>
          <Callout variant="info">
            <strong>{t('sections.postback.testNoteTitle')}</strong> {t('sections.postback.testNoteBody')}
          </Callout>
        </Section>

        <Section id="click-id" title={t('sections.clickId.title')} icon={KeyRound}>
          <p>
            {t('sections.clickId.p1')}{' '}
            <code className="text-purple-300">{config.affiliate.clickIdPlaceholder}</code>
          </p>
          <ol className="list-decimal pl-6 space-y-1.5 text-gray-300">
            <li>
              {t('sections.clickId.li1')}{' '}
              <code className="text-purple-300">
                {config.affiliate.clickIdPlaceholder.replace(/[{}]/g, '')}
              </code>
            </li>
            <li>{t('sections.clickId.li2')}</li>
            <li>{t('sections.clickId.li3')}</li>
          </ol>
          <Callout variant="warn">
            <strong>{t('sections.clickId.warnTitle')}</strong> {t('sections.clickId.warnBody')}
          </Callout>
        </Section>

        <Section id="browser" title={t('sections.browser.title')} icon={Code2}>
          <p>{t('sections.browser.p1')}</p>

          {!config.tracking.googleTagManagerId && (
            <Callout variant="warn">
              <strong>{t('sections.browser.gtm.notConfiguredTitle')}</strong>{' '}
              {t('sections.browser.gtm.notConfiguredBody')}
            </Callout>
          )}

          <Callout variant="info">
            <strong>{t('sections.browser.gtm.benefitsTitle')}</strong>{' '}
            {t('sections.browser.gtm.benefitsBody', {
              count: config.tracking.configuredPixelIds.length,
            })}
          </Callout>

          {/* When the operator has auto-sync linked, show a green
              "Auto-synced" badge so the dev team knows the GTM
              workspace is managed by HitzLink and updates on a
              schedule. The manual download below stays available as
              a fallback for ops teams who want a one-shot snapshot. */}
          {config.tracking.gtmAutoSync?.linked && (
            <Callout variant="ok">
              <strong>{t('sections.browser.gtm.autoSyncedTitle')}</strong>{' '}
              {t('sections.browser.gtm.autoSyncedBody', {
                when: config.tracking.gtmAutoSync.lastSyncAt
                  ? new Date(config.tracking.gtmAutoSync.lastSyncAt).toLocaleString()
                  : '—',
              })}
              {config.tracking.gtmAutoSync.summary && (
                <span className="block text-xs text-emerald-200/80 mt-1">
                  {t('sections.browser.gtm.autoSyncedSummary', {
                    created: config.tracking.gtmAutoSync.summary.created,
                    updated: config.tracking.gtmAutoSync.summary.updated,
                    skipped: config.tracking.gtmAutoSync.summary.skipped,
                  })}
                </span>
              )}
            </Callout>
          )}

          <h3 className="text-sm font-semibold text-white mt-4 mb-1.5">
            <span>{t('sections.browser.h4a')}</span>
          </h3>
          <p className="text-sm text-gray-400 mb-2">{t('sections.browser.gtm.h4aDesc')}</p>
          <CodeBlock label={t('sections.browser.headSnippetLabel')} code={activeSnippets.head} />

          <h3 className="text-sm font-semibold text-white mt-6 mb-1.5">{t('sections.browser.h4b')}</h3>
          <p className="text-sm text-gray-400 mb-2">{t('sections.browser.gtm.h4bDesc')}</p>
          <CodeBlock label={t('sections.browser.regSnippetLabel')} code={activeSnippets.registration} />

          <h3 className="text-sm font-semibold text-white mt-6 mb-1.5">{t('sections.browser.h4c')}</h3>
          <p className="text-sm text-gray-400 mb-2">{t('sections.browser.gtm.h4cDesc')}</p>
          <CodeBlock label={t('sections.browser.depositSnippetLabel')} code={activeSnippets.deposit} />

          <GtmTemplateBlock
            pageTitle={config.page.title}
            pageId={config.page.id}
            pixelIds={config.tracking.configuredPixelIds}
            googleAnalyticsId={config.tracking.googleAnalyticsId}
            gtmId={config.tracking.googleTagManagerId}
          />
        </Section>

        <Section id="attribution" title={t('sections.attribution.title')} icon={Network}>
          <p>{t('sections.attribution.p1')}</p>
          <div className="overflow-x-auto rounded-lg border border-gray-700/50">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-800/50 text-xs uppercase text-gray-400">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('sections.attribution.tableParam')}</th>
                  <th className="px-3 py-2 font-medium">{t('sections.attribution.tableSource')}</th>
                  <th className="px-3 py-2 font-medium">{t('sections.attribution.tableLifetime')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50 text-gray-300">
                <tr>
                  <td className="px-3 py-2"><code>fbp</code></td>
                  <td className="px-3 py-2 text-gray-400">{t('sections.attribution.fbpSource')}</td>
                  <td className="px-3 py-2 text-gray-400">{t('sections.attribution.days90')}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2"><code>fbc</code></td>
                  <td className="px-3 py-2 text-gray-400">{t('sections.attribution.fbcSource')}</td>
                  <td className="px-3 py-2 text-gray-400">{t('sections.attribution.days90')}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2"><code>fbclid</code></td>
                  <td className="px-3 py-2 text-gray-400">{t('sections.attribution.fbclidSource')}</td>
                  <td className="px-3 py-2 text-gray-400">{t('sections.attribution.days90')}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2"><code>{config.affiliate.clickIdPlaceholder.replace(/[{}]/g, '')}</code></td>
                  <td className="px-3 py-2 text-gray-400">{t('sections.attribution.clickIdSource')}</td>
                  <td className="px-3 py-2 text-gray-400">{t('sections.attribution.clickIdLifetime')}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">
            {t('sections.attribution.examplePrefix')}{' '}
            <code className="break-all text-gray-400">
              ?fbp={'{fbp}'}&amp;fbc={'{fbc}'}&amp;fbclid={'{fbclid}'}
              &amp;{config.affiliate.clickIdPlaceholder.replace(/[{}]/g, '')}=abc123
            </code>
          </p>
        </Section>

        <footer className="border-t border-gray-800 pt-6 text-xs text-gray-500">
          <p>
            {t('sections.attribution.footerGenerated')}{' '}
            <code className="text-gray-400">{config.page.id}</code>
          </p>
          <p className="mt-1">
            {t('sections.attribution.footerQuestions')}{' '}
            <a
              href="https://hitzlink.com"
              className="text-blue-400 hover:underline"
              rel="noopener noreferrer"
              target="_blank"
            >
              hitzlink.com
            </a>
            .
          </p>
        </footer>
      </article>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
//  Section building blocks
// ────────────────────────────────────────────────────────────────────

function Section({
  id,
  title,
  icon: Icon,
  children,
}: {
  id: string;
  title: string;
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20 space-y-3">
      <h2 className="flex items-center gap-2 text-xl font-bold text-white">
        <Icon size={20} className="text-blue-400" />
        {title}
      </h2>
      <div className="prose prose-invert prose-sm max-w-none text-gray-300 [&>p]:leading-relaxed [&_code]:rounded [&_code]:bg-gray-800 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[0.9em] [&_code]:text-blue-300">
        {children}
      </div>
    </section>
  );
}

function StatusPills({ config }: { config: DevDocConfig }) {
  const { t } = useTranslation('devDoc');
  const { primaryPixelId, googleAnalyticsId, configuredPixelIds } = config.tracking;
  return (
    <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
      <Pill
        active={Boolean(primaryPixelId)}
        label={
          primaryPixelId
            ? t('status.pixelSet', { id: primaryPixelId })
            : t('status.pixelNotSet')
        }
        accent="blue"
      />
      <Pill
        active={Boolean(googleAnalyticsId)}
        label={
          googleAnalyticsId
            ? t('status.gaSet', { id: googleAnalyticsId })
            : t('status.gaNotSet')
        }
        accent="orange"
      />
      {configuredPixelIds.length > 1 && (
        <Pill
          active
          label={t('status.morePixels', { count: configuredPixelIds.length - 1 })}
          accent="purple"
        />
      )}
    </div>
  );
}

function Pill({
  active,
  label,
  accent,
}: {
  active: boolean;
  label: string;
  accent: 'blue' | 'orange' | 'purple';
}) {
  const cls = active
    ? {
        blue: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
        orange: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
        purple: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
      }[accent]
    : 'bg-gray-800/40 text-gray-500 border-gray-700/50';
  return (
    <span className={`rounded-full border px-2.5 py-0.5 ${cls}`}>{label}</span>
  );
}

function PostbackRow({
  label,
  description,
  url,
  event,
  pageId,
  token,
  accent,
}: {
  label: string;
  description: string;
  url: string;
  event: 'registration' | 'ftd' | 'purchase';
  pageId: string;
  token: string;
  accent: 'green' | 'purple' | 'gray';
}) {
  const { t } = useTranslation('devDoc');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [testErr, setTestErr] = useState<string | null>(null);

  const accentClass = {
    green: 'border-green-500/30 bg-green-500/5',
    purple: 'border-purple-500/30 bg-purple-500/5',
    gray: 'border-gray-700/50 bg-gray-800/30',
  }[accent];

  async function runTest() {
    try {
      setTesting(true);
      setResult(null);
      setTestErr(null);
      const res = await fetch(
        `${API_URL}/capi/dev-doc/${pageId}/test-postback?token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        setTestErr(body.error || `HTTP ${res.status}`);
        return;
      }
      const data: TestResult = await res.json();
      setResult(data);
    } catch (e) {
      setTestErr(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className={`my-3 rounded-lg border p-3 ${accentClass}`}>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-white">{label}</div>
          <div className="text-xs text-gray-400">{description}</div>
        </div>
        <div className="flex items-center gap-2">
          <CopyButton text={url} />
          <button
            type="button"
            onClick={runTest}
            disabled={testing}
            className="inline-flex items-center gap-1 rounded bg-gray-900/60 px-2 py-0.5 text-[11px] text-gray-300 transition hover:bg-gray-900 disabled:opacity-50"
            title={t('postbackRow.tryTitle')}
          >
            {testing ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                {t('postbackRow.testing')}
              </>
            ) : (
              <>
                <PlayCircle size={12} />
                {t('postbackRow.tryIt')}
              </>
            )}
          </button>
        </div>
      </div>
      <code className="block break-all rounded bg-gray-950/60 p-2 font-mono text-[11px] text-gray-300 select-all">
        {url}
      </code>
      {result && (
        <div
          className={`mt-2 rounded-md border p-2 text-xs ${
            result.ok
              ? 'border-green-500/30 bg-green-500/10 text-green-200'
              : 'border-yellow-500/30 bg-yellow-500/10 text-yellow-200'
          }`}
        >
          <div className="flex items-center gap-1.5 font-semibold">
            {result.ok ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
            {result.ok
              ? t('postbackRow.okStatus')
              : t('postbackRow.failedStatus', { status: result.status })}
          </div>
          <div className="mt-1 text-[11px] opacity-90">
            {t('postbackRow.responseBody')} <code className="text-gray-300">{result.body}</code>
          </div>
          {result.isExpectedClickNotFound && (
            <div className="mt-1 text-[10px] text-gray-400">
              ✓ <code>click_not_found</code> {t('postbackRow.syntheticExpected')}
            </div>
          )}
        </div>
      )}
      {testErr && (
        <div className="mt-2 rounded-md border border-red-500/30 bg-red-500/10 p-2 text-xs text-red-200">
          <div className="flex items-center gap-1.5 font-semibold">
            <AlertCircle size={12} />
            {t('postbackRow.testFailed')}
          </div>
          <div className="mt-1 text-[11px]">{testErr}</div>
        </div>
      )}
    </div>
  );
}

function CodeBlock({
  label,
  code,
  hint,
}: {
  label: string;
  code: string;
  hint?: string;
}) {
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-gray-700/50 bg-gray-950">
      <div className="flex items-center justify-between border-b border-gray-800 bg-gray-900/60 px-3 py-1.5">
        <span className="text-[11px] font-medium text-gray-400">{label}</span>
        <CopyButton text={code} />
      </div>
      <pre className="max-h-96 overflow-x-auto p-3 text-[11px] leading-relaxed text-gray-200">
        <code>{code}</code>
      </pre>
      {hint && (
        <div className="border-t border-gray-800 bg-gray-900/40 px-3 py-1.5 text-[10px] text-gray-500">
          {hint}
        </div>
      )}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation('devDoc');
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1 rounded bg-gray-900/60 px-2 py-0.5 text-[11px] text-gray-300 transition hover:bg-gray-900"
    >
      {copied ? (
        <>
          <CheckCircle2 size={12} className="text-green-400" />
          {t('copy.copied')}
        </>
      ) : (
        <>
          <Copy size={12} />
          {t('copy.copy')}
        </>
      )}
    </button>
  );
}

/**
 * Download-and-import block for the GTM container template. The
 * template carries all configured pixels + GA4 — re-download after
 * changing the pixel set in HitzLink and re-import in GTM to push
 * the changes.
 */
function GtmTemplateBlock({
  pageTitle,
  pageId,
  pixelIds,
  googleAnalyticsId,
  gtmId,
}: {
  pageTitle: string;
  pageId: string;
  pixelIds: string[];
  googleAnalyticsId: string;
  gtmId: string;
}) {
  const { t } = useTranslation('devDoc');
  const [downloaded, setDownloaded] = useState(false);
  const cleanPixels = pixelIds.filter((id) => id && id.trim());
  const canDownload = cleanPixels.length > 0 || Boolean(googleAnalyticsId);

  function download() {
    downloadGtmContainerTemplate({
      pageTitle,
      pageId,
      pixelIds: cleanPixels,
      googleAnalyticsId,
      gtmId,
    });
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 2400);
  }

  return (
    <div className="my-4 rounded-lg border border-purple-500/30 bg-purple-500/5 p-4">
      <div className="flex items-center gap-2 mb-2">
        <FileJson size={16} className="text-purple-300" />
        <h4 className="text-sm font-semibold text-white">
          {t('sections.browser.gtm.templateTitle')}
        </h4>
      </div>
      <p className="text-xs text-gray-300 mb-3 leading-relaxed">
        {t('sections.browser.gtm.templateBody', {
          pixelCount: cleanPixels.length,
          ga: googleAnalyticsId ? ` + GA4 ${googleAnalyticsId}` : '',
        })}
      </p>

      <button
        type="button"
        onClick={download}
        disabled={!canDownload}
        className={[
          'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition',
          canDownload
            ? 'bg-purple-600 text-white hover:bg-purple-700'
            : 'bg-gray-800 text-gray-500 cursor-not-allowed',
        ].join(' ')}
      >
        {downloaded ? (
          <>
            <CheckCircle2 size={14} />
            {t('sections.browser.gtm.templateDownloaded')}
          </>
        ) : (
          <>
            <Download size={14} />
            {t('sections.browser.gtm.templateButton')}
          </>
        )}
      </button>

      <details className="mt-3 text-xs text-gray-400">
        <summary className="cursor-pointer text-gray-300 hover:text-white">
          {t('sections.browser.gtm.howToImport')}
        </summary>
        <ol className="mt-2 list-decimal pl-5 space-y-1 leading-relaxed">
          <li>{t('sections.browser.gtm.import.s1')}</li>
          <li>{t('sections.browser.gtm.import.s2')}</li>
          <li>{t('sections.browser.gtm.import.s3')}</li>
          <li>{t('sections.browser.gtm.import.s4')}</li>
          <li>{t('sections.browser.gtm.import.s5')}</li>
        </ol>
      </details>
    </div>
  );
}

function Callout({
  variant,
  children,
}: {
  variant: 'info' | 'warn' | 'ok';
  children: React.ReactNode;
}) {
  let cls: string;
  let Icon: LucideIcon;
  switch (variant) {
    case 'info':
      cls = 'border-blue-500/30 bg-blue-500/10 text-blue-100';
      Icon = AlertCircle;
      break;
    case 'warn':
      cls = 'border-amber-500/30 bg-amber-500/10 text-amber-100';
      Icon = AlertCircle;
      break;
    case 'ok':
      cls = 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100';
      Icon = CheckCircle2;
      break;
  }
  return (
    <div className={`my-3 flex items-start gap-2 rounded-lg border p-3 text-sm ${cls}`}>
      <Icon size={14} className="mt-0.5 flex-shrink-0" />
      <div className="text-[13px] leading-relaxed">{children}</div>
    </div>
  );
}

function ExportButtons({
  docMarkdown,
  pageTitle,
}: {
  docMarkdown: string;
  pageTitle: string;
}) {
  const { t } = useTranslation('devDoc');
  const [copied, setCopied] = useState(false);

  function copyAll() {
    navigator.clipboard.writeText(docMarkdown).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function download() {
    const safe = pageTitle.replace(/[^a-z0-9]+/gi, '-').toLowerCase().slice(0, 60);
    const filename = `hitzlink-${safe || 'integration'}.md`;
    const blob = new Blob([docMarkdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={copyAll}
        className="flex w-full items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700"
      >
        {copied ? (
          <>
            <CheckCircle2 size={14} />
            {t('export.copiedDoc')}
          </>
        ) : (
          <>
            <Copy size={14} />
            {t('export.copyMarkdown')}
          </>
        )}
      </button>
      <button
        type="button"
        onClick={download}
        className="flex w-full items-center gap-2 rounded-md border border-gray-700 bg-gray-800/40 px-3 py-1.5 text-xs font-medium text-gray-300 transition hover:bg-gray-800"
      >
        <Download size={14} />
        {t('export.downloadMd')}
      </button>
      <a
        href="https://www.facebook.com/business/help/2041148702652965"
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-xs text-gray-500 transition hover:text-gray-300"
      >
        <ExternalLink size={14} />
        {t('export.metaCapiDocs')}
      </a>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
//  Markdown export
// ────────────────────────────────────────────────────────────────────

function buildFullMarkdownDoc(
  config: DevDocConfig,
  args: {
    snippets: { head: string; registration: string; deposit: string };
  },
  t: TFunction<'devDoc'>
): string {
  const { snippets } = args;
  const cidPlaceholder = config.affiliate.clickIdPlaceholder;
  const cidName = cidPlaceholder.replace(/[{}]/g, '');
  const lines: string[] = [];

  lines.push(`# ${config.page.title} — ${t('markdown.docTitleSuffix')}`);
  lines.push('');
  lines.push(`> ${t('markdown.blurb')}`);
  lines.push('');
  lines.push(t('markdown.pageIdLine', { id: config.page.id }));
  if (config.tracking.primaryPixelId) {
    lines.push(t('markdown.pixelLine', { id: config.tracking.primaryPixelId }));
  }
  if (config.tracking.googleAnalyticsId) {
    lines.push(t('markdown.gaLine', { id: config.tracking.googleAnalyticsId }));
  }
  if (config.tracking.googleTagManagerId) {
    lines.push(t('markdown.gtmLine', { id: config.tracking.googleTagManagerId }));
  }
  lines.push('');

  lines.push(`## ${t('sections.overview.title')}`);
  lines.push('');
  lines.push(t('markdown.overviewBody'));
  lines.push('');

  lines.push(`## ${t('sections.postback.title')}`);
  lines.push('');
  lines.push(`### ${t('sections.postback.registrationLabel')}`);
  lines.push('```');
  lines.push(config.postbackUrls.registration);
  lines.push('```');
  lines.push('');
  lines.push(`### ${t('sections.postback.ftdLabel')}`);
  lines.push('```');
  lines.push(config.postbackUrls.ftd);
  lines.push('```');
  lines.push('');
  lines.push(`### ${t('sections.postback.customTemplateLabel')}`);
  lines.push('```');
  lines.push(config.postbackUrls.custom);
  lines.push('```');
  lines.push('');

  lines.push(`## ${t('sections.clickId.title')}`);
  lines.push('');
  lines.push(
    t('markdown.clickIntro', { cidName, cidPlaceholder })
  );
  lines.push('');
  lines.push(t('markdown.clickLi1', { cidName }));
  lines.push(t('markdown.clickLi2'));
  lines.push(t('markdown.clickLi3'));
  lines.push('');
  lines.push(t('markdown.clickWarn'));
  lines.push('');

  lines.push(`## ${t('sections.browser.title')}`);
  lines.push('');
  lines.push(t('markdown.gtmModeNotice'));
  lines.push('');
  lines.push(t('markdown.browser41'));
  lines.push('```html');
  lines.push(snippets.head);
  lines.push('```');
  lines.push('');
  lines.push(t('markdown.browser42'));
  lines.push('```html');
  lines.push(snippets.registration);
  lines.push('```');
  lines.push('');
  lines.push(t('markdown.browser43'));
  lines.push(t('markdown.depositReplace'));
  lines.push('```html');
  lines.push(snippets.deposit);
  lines.push('```');
  lines.push('');
  lines.push(t('markdown.gtmTemplateNotice'));
  lines.push('');

  lines.push(`## ${t('sections.attribution.title')}`);
  lines.push('');
  lines.push(t('markdown.tableHeader'));
  lines.push(t('markdown.tableSep'));
  lines.push(t('markdown.tableFbp'));
  lines.push(t('markdown.tableFbc'));
  lines.push(t('markdown.tableFbclid'));
  lines.push(t('markdown.tableClick', { cidName }));
  lines.push('');

  return lines.join('\n');
}
